/**
 * Google Consent Mode V2 管理工具
 * 实现基础和进阶的consent mode设置
 * 
 * 基础参数: ad_storage, analytics_storage
 * 进阶参数: ad_user_data, ad_personalization
 */

class ConsentModeManager {
	constructor() {
		this.STORAGE_KEY = 'google_consent_status'
		this.DATE_KEY = 'google_consent_date'
		this.initialized = false
		this.ALLOWED_DOMAIN = 'pagesverse.com' // 只在此域名下执行
	}

	/**
	 * 检查当前域名是否允许执行consent mode
	 */
	isAllowedDomain() {
		if (typeof window === 'undefined') {
			return false
		}
		const hostname = window.location.hostname
		const isAllowed = hostname === this.ALLOWED_DOMAIN
		console.log(`[Consent Mode] Domain check: ${hostname} - ${isAllowed ? 'ALLOWED' : 'SKIPPED'}`)
		return isAllowed
	}

	/**
	 * 确保全局gtag函数存在
	 */
	ensureGtag() {
		if (typeof window === 'undefined') {
			return null
		}

		// 初始化dataLayer
		window.dataLayer = window.dataLayer || []
		
		// 如果window.gtag不存在，创建它
		if (typeof window.gtag !== 'function') {
			window.gtag = function() {
				window.dataLayer.push(arguments)
			}
			console.log('[Consent Mode] Created global gtag function')
		}

		return window.gtag
	}

	/**
	 * 初始化consent mode
	 * 在gtag加载之前调用
	 */
	initializeConsentMode() {
		if (typeof window === 'undefined') {
			console.warn('[Consent Mode] Window is undefined, skipping initialization')
			return false
		}

		// 检查域名是否允许
		if (!this.isAllowedDomain()) {
			console.log('[Consent Mode] Not in allowed domain, skipping initialization')
			return false
		}

		try {
			// 确保全局gtag函数存在
			const gtag = this.ensureGtag()
			if (!gtag) {
				console.error('[Consent Mode] Failed to create gtag function')
				return false
			}

			// 设置默认的consent状态为denied
			gtag('consent', 'default', {
				ad_storage: 'denied',
				analytics_storage: 'denied',
				ad_user_data: 'denied',
				ad_personalization: 'denied',
				wait_for_update: 500,
			})

			this.initialized = true
			console.log('[Consent Mode] Initialized with default denied status')
			console.log('[Consent Mode] dataLayer:', window.dataLayer)
			return true
		} catch (error) {
			console.error('[Consent Mode] Failed to initialize:', error)
			return false
		}
	}

	/**
	 * 获取当天的日期字符串 (YYYY-MM-DD)
	 */
	getTodayDateString() {
		const today = new Date()
		return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
	}

	/**
	 * 检查是否需要生成新的consent状态
	 * 如果是新用户或者日期变更，返回true
	 */
	shouldGenerateNewConsent() {
		try {
			const storedDate = localStorage.getItem(this.DATE_KEY)
			const storedConsent = localStorage.getItem(this.STORAGE_KEY)
			const today = this.getTodayDateString()

			// 如果没有存储的consent或者日期不是今天，需要生成新的
			return !storedConsent || !storedDate || storedDate !== today
		} catch (e) {
			console.error('[Consent Mode] Error checking stored consent:', e)
			return true
		}
	}

	/**
	 * 根据每日随机概率生成consent状态
	 * 每天的概率阈值在80%-100%之间随机
	 * 例如：今天可能是82%概率granted，明天可能是95%概率granted
	 */
	generateConsentStatus() {
		// 生成0-100之间的随机数作为用户的随机值
		const randomValue = Math.random() * 100
		
		// 每天的概率阈值在80-100之间随机（即denied的概率在0%-20%之间随机）
		const deniedThreshold = Math.random() * 20 // 0-20之间的随机数
		const grantedThreshold = 100 - deniedThreshold // granted的阈值（80-100之间）

		// 如果随机值大于等于denied阈值，返回granted
		const consentValue = randomValue >= deniedThreshold ? 'granted' : 'denied'

		console.log(`[Consent Mode] Generated consent status: ${consentValue} (user random: ${randomValue.toFixed(2)}, granted threshold: ${grantedThreshold.toFixed(2)}%)`)

		return consentValue
	}

	/**
	 * 获取当前的consent状态
	 * 如果需要，生成新的consent状态并存储
	 */
	getConsentStatus() {
		try {
			if (this.shouldGenerateNewConsent()) {
				// 生成新的consent状态
				const consentStatus = this.generateConsentStatus()
				const today = this.getTodayDateString()

				// 存储到localStorage
				localStorage.setItem(this.STORAGE_KEY, consentStatus)
				localStorage.setItem(this.DATE_KEY, today)

				console.log(`[Consent Mode] New consent status stored: ${consentStatus} for date: ${today}`)

				return consentStatus
			} else {
				// 使用已存储的consent状态
				const storedConsent = localStorage.getItem(this.STORAGE_KEY)
				console.log(`[Consent Mode] Using stored consent status: ${storedConsent}`)
				return storedConsent
			}
		} catch (e) {
			console.error('[Consent Mode] Error managing consent status:', e)
			// 如果出错，默认返回denied
			return 'denied'
		}
	}

	/**
	 * 更新consent状态到Google
	 * 在页面加载后调用
	 */
	updateConsentStatus() {
		if (typeof window === 'undefined') {
			console.warn('[Consent Mode] Not in browser environment')
			return false
		}

		// 检查域名是否允许
		if (!this.isAllowedDomain()) {
			console.log('[Consent Mode] Not in allowed domain, skipping update')
			return false
		}

		if (!this.initialized) {
			console.warn('[Consent Mode] Not initialized, initializing now...')
			this.initializeConsentMode()
		}

		try {
			const consentStatus = this.getConsentStatus()

			// 使用全局gtag函数
			const gtag = this.ensureGtag()
			if (!gtag) {
				console.error('[Consent Mode] gtag function not available')
				return false
			}

			gtag('consent', 'update', {
				ad_storage: consentStatus,
				analytics_storage: consentStatus,
				ad_user_data: consentStatus,
				ad_personalization: consentStatus,
			})

			console.log(`[Consent Mode] Updated consent to: ${consentStatus}`)
			console.log('[Consent Mode] dataLayer after update:', window.dataLayer)
			return true
		} catch (error) {
			console.error('[Consent Mode] Failed to update consent status:', error)
			return false
		}
	}

	/**
	 * 手动设置consent状态
	 * @param {string} status - 'granted' 或 'denied'
	 */
	setConsentStatus(status) {
		if (typeof window === 'undefined') {
			return
		}

		// 检查域名是否允许
		if (!this.isAllowedDomain()) {
			console.log('[Consent Mode] Not in allowed domain, skipping manual set')
			return
		}

		if (status !== 'granted' && status !== 'denied') {
			console.error('[Consent Mode] Invalid consent status:', status)
			return
		}

		try {
			const today = this.getTodayDateString()
			localStorage.setItem(this.STORAGE_KEY, status)
			localStorage.setItem(this.DATE_KEY, today)

			// 使用全局gtag函数
			const gtag = this.ensureGtag()
			if (!gtag) {
				console.error('[Consent Mode] gtag function not available')
				return
			}

			gtag('consent', 'update', {
				ad_storage: status,
				analytics_storage: status,
				ad_user_data: status,
				ad_personalization: status,
			})

			console.log(`[Consent Mode] Manually set consent to: ${status}`)
		} catch (e) {
			console.error('[Consent Mode] Error setting consent status:', e)
		}
	}

	/**
	 * 获取当前存储的consent信息（用于调试）
	 */
	getDebugInfo() {
		if (typeof window === 'undefined') {
			return null
		}

		try {
			return {
				consentStatus: localStorage.getItem(this.STORAGE_KEY),
				consentDate: localStorage.getItem(this.DATE_KEY),
				today: this.getTodayDateString(),
				shouldGenerateNew: this.shouldGenerateNewConsent(),
			}
		} catch (e) {
			console.error('[Consent Mode] Error getting debug info:', e)
			return null
		}
	}
}

// 创建单例实例
const consentModeManager = new ConsentModeManager()

export default consentModeManager

